import { create } from "zustand";
import { subscribeWithSelector } from "zustand/middleware";

export type PaymentStep =
  | "redirecting"
  | "processing"
  | "success"
  | "error"
  | "cancel";

// Which message the success step should render. "payment" is the default
// post-checkout success; "upgrade"/"downgrade" reuse the same modal for a
// subscription plan change.
export type PaymentSuccessVariant = "payment" | "upgrade" | "downgrade";

export interface PaymentState {
  // Modal state
  isPaymentModalOpen: boolean;

  // Current step
  currentStep: PaymentStep;

  // Which message the "success" step should display
  successVariant: PaymentSuccessVariant;

  // Payment provider
  paymentProvider: string | null;

  // Session data
  sessionId: string | null;
  sessionUrl: string | null;

  // Error handling
  error: string | null;

  // Loading states
  isLoading: boolean;
  isRedirecting: boolean;
}

export interface PaymentActions {
  // Modal actions
  openPaymentModal: (
    provider: string,
    sessionId?: string,
    sessionUrl?: string,
    step?: PaymentStep
  ) => void;
  closePaymentModal: () => void;

  // Open the success modal for a subscription plan change (no checkout session)
  openSubscriptionChangeModal: (
    variant: Exclude<PaymentSuccessVariant, "payment">
  ) => void;

  // Step navigation
  setCurrentStep: (step: PaymentStep) => void;

  // Session management
  setSessionId: (sessionId: string) => void;
  setSessionData: (sessionId: string, sessionUrl: string) => void;
  clearSessionData: () => void;

  // Error handling
  setError: (error: string) => void;
  clearError: () => void;

  // Loading states
  setLoading: (loading: boolean) => void;
  setRedirecting: (redirecting: boolean) => void;

  // Reset
  resetPayment: () => void;
}

export type PaymentCoreStore = PaymentState & PaymentActions;

const initialState: PaymentState = {
  isPaymentModalOpen: false,
  currentStep: "redirecting",
  successVariant: "payment",
  paymentProvider: null,
  sessionId: null,
  sessionUrl: null,
  error: null,
  isLoading: false,
  isRedirecting: false,
};

export const usePaymentCoreStore = create<PaymentCoreStore>()(
  subscribeWithSelector((set) => ({
    ...initialState,

    // Modal actions
    openPaymentModal: (
      provider: string,
      sessionId?: string,
      sessionUrl?: string,
      step?: PaymentStep
    ) => {
      set({
        isPaymentModalOpen: true,
        paymentProvider: provider,
        currentStep: step || "redirecting",
        successVariant: "payment",
        sessionId: sessionId || null,
        sessionUrl: sessionUrl || null,
        error: null,
        isLoading: false,
        isRedirecting: step === "redirecting",
      });
    },

    closePaymentModal: () => {
      set({
        isPaymentModalOpen: false,
        currentStep: "redirecting",
        successVariant: "payment",
        paymentProvider: null,
        sessionId: null,
        sessionUrl: null,
        error: null,
        isLoading: false,
        isRedirecting: false,
      });
    },

    openSubscriptionChangeModal: (
      variant: Exclude<PaymentSuccessVariant, "payment">
    ) => {
      set({
        isPaymentModalOpen: true,
        paymentProvider: "subscription",
        currentStep: "success",
        successVariant: variant,
        sessionId: null,
        sessionUrl: null,
        error: null,
        isLoading: false,
        isRedirecting: false,
      });
    },

    // Step navigation
    setCurrentStep: (step: PaymentStep) => {
      set({ currentStep: step });
    },

    // Session management
    setSessionData: (sessionId: string, sessionUrl: string) => {
      set({ sessionId, sessionUrl });
    },

    // Session management
    setSessionId: (sessionId: string) => {
      set({ sessionId });
    },

    clearSessionData: () => {
      set({ sessionId: null, sessionUrl: null });
    },

    // Error handling
    setError: (error: string) => {
      set({ error, currentStep: "error" });
    },

    clearError: () => {
      set({ error: null });
    },

    // Loading states
    setLoading: (loading: boolean) => {
      set({ isLoading: loading });
    },

    setRedirecting: (redirecting: boolean) => {
      set({ isRedirecting: redirecting });
    },

    // Reset
    resetPayment: () => {
      set(initialState);
    },
  }))
);
